import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  InputLabel as Label,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import InputLabel from 'components/Input';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectFilterAction } from 'store/actions/filterActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Scope from 'types/entities/Scope';
import CloseIcon from '@mui/icons-material/Close';

interface SimpleIdNameOption {
  id: number;
  name: string;
}

type ISearchHeaderCellOptions = SimpleIdNameOption[];
// type ISearchHeaderCellOptions = Scope[] | undefined;

type SearchHeaderCellProps = {
  field: string;
  options?: ISearchHeaderCellOptions;
  headerName: string;
  disabled?: boolean;
};
const SearchHeaderCell = React.memo((props: SearchHeaderCellProps) => {
  const { field, options, headerName, disabled = false } = props;

  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const filter = useSelector((state) => state.filter);
  const searchValue = filter.selected?.[field]?.[0] ?? '';
  const [inputValue, setInputValue] = useState(searchValue);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchUpdate(field, inputValue);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  const searchType = useCallback(() => {
    switch (field) {
      case 'scope_id':
      case 'id':
      case 'purchase_order':
      case 'mission_advancement_id':
      case 'workunit_reference':
      case 'label':
        return 'select';
      case 'forecast_date':
      case 'delivery_date':
        return 'date';
      default:
        return 'text';
    }
  }, [field]);

  const searchUpdate = useCallback(
    (field: string, value: any) => {
      if (field && value) dispatch(selectFilterAction({ [field]: [value] }));
      else dispatch(selectFilterAction({ [field]: [] }));
    },
    [dispatch]
  );

  const renderInput = () => {
    if (searchType() === 'select' && options && options?.length > 0) {
      return (
        <FormControl fullWidth>
          <label htmlFor={`select_search_${field}`}>{t(headerName)}</label>
          <Select
            labelId={`select_search_${field}`}
            id={`$current_${field}`}
            disabled={disabled}
            placeholder={t(field)}
            onChange={(e) => {
              e.stopPropagation();
              setInputValue(e.target.value);
            }}
            onBlur={(e) => e.stopPropagation()}
            value={inputValue || ''}
            fullWidth
            input={
              <OutlinedInput
                endAdornment={
                  inputValue ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        // edge="end"
                        sx={{ zIndex: 1, mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setInputValue('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
            }
            componentsProps={{}}
            sx={{
              '.MuiSelect-select': {
                p: 0.5,
              },
              '.MuiSelect-icon': {
                position: 'absolute',
                right: 8, // dropdown arrow position
                zIndex: 2, // below close icon
              },
            }}
          >
            {/* <MenuItem value={0}>None</MenuItem> */}
            {options.map((option) => (
              <MenuItem value={option.id}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <>
        <label htmlFor={`input_header_${field}`}>{t(field)}</label>
        <TextField
          onChange={(newValue) => {
            setInputValue(newValue.target.value);
          }}
          id={`input_header_${field}`}
          value={inputValue}
          type={searchType()}
          required
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton
                sx={{ visibility: inputValue ? 'visible' : 'hidden', padding: 0 }}
                onClick={() => setInputValue('')}
                color="primary"
              >
                <ClearIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            ),
            style: { fontSize: 12 },
            className: 'input_head_grid_search',
          }}
          sx={{ padding: 0, fontSize: '0.5rem', '&.Mui-focused .MuiIconButton-root': { color: 'primary.main' } }}
        />
      </>
    );
  };

  return <Box sx={{ display: 'block', width: '100%' }}>{renderInput()}</Box>;
});

export default SearchHeaderCell;
